// 维护公告（消息中心 SERVICE_ANNOUNCEMENT）的前端解析与预览文案生成。
// 模板规则与后端 MaintenanceNoticeService 保持一致：服务端发布时生成正式文案，
// 这里的 buildPayload 仅用于管理端发布前的实时预览。
export const MAINTENANCE_TITLE = '停机维护公告'
export const MAINTENANCE_EVENT_TYPE = 'SERVICE_ANNOUNCEMENT_PUBLISHED'

const WEEKDAYS = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']
const pad = value => String(value).padStart(2, '0')

export function parseInputTime(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})$/.exec(String(value || '').trim())
  if (!match) return null
  const time = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), Number(match[4]), Number(match[5]))
  return Number.isNaN(time.getTime()) ? null : time
}

export function startText(value) {
  const time = value instanceof Date ? value : parseInputTime(value)
  if (!time) return ''
  return `${time.getMonth() + 1}月${time.getDate()}日（${WEEKDAYS[time.getDay()]}）${pad(time.getHours())}:${pad(time.getMinutes())}`
}

export function endText(start, end) {
  const startTime = start instanceof Date ? start : parseInputTime(start)
  const endTime = end instanceof Date ? end : parseInputTime(end)
  if (!startTime || !endTime) return ''
  const clock = `${pad(endTime.getHours())}:${pad(endTime.getMinutes())}`
  return startTime.toDateString() === endTime.toDateString() ? `当日 ${clock}` : `${endTime.getMonth() + 1}月${endTime.getDate()}日 ${clock}`
}

export function durationText(start, end) {
  const startTime = start instanceof Date ? start : parseInputTime(start)
  const endTime = end instanceof Date ? end : parseInputTime(end)
  if (!startTime || !endTime) return ''
  const minutes = Math.max(0, Math.round((endTime.getTime() - startTime.getTime()) / 60000))
  if (minutes < 60) return `预计耗时约 ${minutes} 分钟`
  const hours = Math.round(minutes / 30) / 2
  return `预计耗时约 ${hours} 小时`
}

export const MAINTENANCE_SUMMARY = '我们将在此时段进行系统维护，期间平台暂时无法访问，给您带来不便敬请谅解。'

const FIXED_ITEMS = [
  '维护期间整个平台无法访问，AI 分析、数据集浏览与下载将暂停使用。',
  '仍在运行的分析任务会随维护中断，需在恢复后重新发起，请提前安排任务计划。',
  '对话历史与已完成的分析结果不受影响，恢复访问后可正常查看。',
]

/** 管理端实时预览用；与后端发布的正式文案同一套规则。 */
export function buildPayload(maintenanceStart, maintenanceEnd, remark) {
  const start = parseInputTime(maintenanceStart)
  const end = parseInputTime(maintenanceEnd)
  if (!start || !end || end.getTime() <= start.getTime()) return null
  const trimmedRemark = String(remark || '').trim()
  const items = [...FIXED_ITEMS, `本次维护${durationText(start, end)}，如提前完成将提前恢复访问。`]
  if (trimmedRemark) items.push(trimmedRemark)
  return {
    maintenanceStart,
    maintenanceEnd,
    maintenanceStartText: startText(start),
    maintenanceEndText: endText(start, end),
    remark: trimmedRemark,
    items,
  }
}

/** 解析消息 content.body 里的结构化文案；解析失败返回 null，调用方降级为纯文本。 */
export function parsePayload(body) {
  if (typeof body !== 'string' || !body.trim()) return null
  let root
  try { root = JSON.parse(body) } catch { return null }
  if (!root || typeof root !== 'object' || Array.isArray(root)) return null
  const start = parseInputTime(root.maintenanceStart)
  const end = parseInputTime(root.maintenanceEnd)
  if (!start || !end || !Array.isArray(root.items)) return null
  const items = root.items.filter(item => typeof item === 'string' && item.trim())
  if (!items.length) return null
  return {
    maintenanceStart: root.maintenanceStart,
    maintenanceEnd: root.maintenanceEnd,
    maintenanceStartText: typeof root.maintenanceStartText === 'string' && root.maintenanceStartText ? root.maintenanceStartText : startText(start),
    maintenanceEndText: typeof root.maintenanceEndText === 'string' && root.maintenanceEndText ? root.maintenanceEndText : endText(start, end),
    remark: typeof root.remark === 'string' ? root.remark : '',
    items,
    expired: end.getTime() <= Date.now(),
  }
}

/** 维护窗口阶段：preview=预告期（弹窗），active=停机窗口内（全屏维护门），expired=已结束。 */
export function maintenanceWindowPhase(payload, now = Date.now()) {
  if (!payload) return 'none'
  const start = parseInputTime(payload.maintenanceStart)
  const end = parseInputTime(payload.maintenanceEnd)
  if (!start || !end) return 'none'
  const at = now instanceof Date ? now.getTime() : now
  if (at < start.getTime()) return 'preview'
  if (at < end.getTime()) return 'active'
  return 'expired'
}
